import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';

const ExitExamVerificationPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showComplaintModal, setShowComplaintModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [complaintData, setComplaintData] = useState({
    category: '',
    description: '',
    phoneNumber: ''
  });
  const [complaintReference, setComplaintReference] = useState('');
  const [copyConfirmed, setCopyConfirmed] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isSubmittingComplaint, setIsSubmittingComplaint] = useState(false);

  const complaintCategories = [
    { value: 'result_not_found', label: 'My result not found' },
    { value: 'disqualified_no_reason', label: 'My result disqualified without reason' },
    { value: 'attendance_ng_grade', label: "I've signed attendance but my grade is NG" },
    { value: 'wrong_gender', label: 'Wrong gender information' },
    { value: 'other', label: 'Other' }
  ];

  // Helper function to sort results by year (descending) and period (End Year before Mid Year)
  const sortResultsByYear = (results) => {
    if (!Array.isArray(results)) {
      return results;
    }
    
    return [...results].sort((a, b) => {
      // Extract year from year field (e.g., "2018 Mid Year" -> 2018)
      const getYear = (yearStr) => {
        if (!yearStr) return 0;
        const match = yearStr.toString().match(/(\d{4})/);
        return match ? parseInt(match[1]) : 0;
      };
      
      // Extract period priority (End Year = 1, Mid Year = 2, so End Year comes first for same year)
      const getPeriodPriority = (yearStr) => {
        if (!yearStr) return 0;
        const str = yearStr.toString().toLowerCase();
        if (str.includes('end')) return 1;
        if (str.includes('mid')) return 2;
        return 0;
      };
      
      const yearA = getYear(a.year);
      const yearB = getYear(b.year);
      
      // First sort by year (descending - newest first)
      if (yearB !== yearA) {
        return yearB - yearA;
      }
      
      // If same year, sort by period (End Year before Mid Year)
      const periodA = getPeriodPriority(a.year);
      const periodB = getPeriodPriority(b.year);
      return periodA - periodB;
    });
  };

  const is2018EndYearPendingInResults = (results) => {
    const items = Array.isArray(results)
      ? results
      : results && typeof results === 'object'
        ? [results]
        : [];

    return items.some((item) => {
      const is2018EndYear = String(item?.year || '').trim() === '2018 End Year';
      const isPending =
        typeof item?.total_score === 'string' &&
        item.total_score.trim().toLowerCase() === 'result on pending';
      return is2018EndYear && isPending;
    });
  };

  useEffect(() => {
    const fetchResultByUsername = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!username) {
          setError('Invalid verification link');
          return;
        }

        const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
        const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

        const response = await fetch(`${API_URL}/exit-exam-result`, {
          method: 'POST',
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            username: username
          })
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('Certificate not found or invalid username');
          } else if (response.status === 401) {
            throw new Error('Invalid API key');
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const data = await response.json();
        
        if (data.status === 'success' && data.data) {
          setResult(data.data);
        } else {
          throw new Error(data.message || 'Failed to fetch result');
        }
      } catch (err) {
        console.error('Error fetching result by username:', err);
        setError(err.message || 'Failed to load certificate data');
      } finally {
        setLoading(false);
      }
    };

    fetchResultByUsername();
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: 'rgb(18,82,166)' }}></div>
            <p className="text-gray-600">Loading certificate...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-red-500 text-6xl mb-4">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'rgb(18,82,166)' }}
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md w-full mx-4">
            <div className="text-gray-500 text-6xl mb-4">
              <i className="fa-solid fa-file-circle-question"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Certificate Found</h2>
            <p className="text-gray-600 mb-6">No certificate data available for this username</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 text-white rounded-lg transition-colors"
              style={{ backgroundColor: 'rgb(18,82,166)' }}
            >
              Go Back Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Get results array, default to empty array if not present
  let resultsArray = [];
  if (result && result.results) {
    if (Array.isArray(result.results)) {
      resultsArray = result.results;
    } else if (typeof result.results === 'object') {
      resultsArray = [result.results];
    }
  }
  
  // Sort results by year in descending order (newest first)
  resultsArray = sortResultsByYear(resultsArray);

  // Temporarily enabled regardless of backend complaint_status, except 2018 End Year pending
  const is2018EndYearPending = is2018EndYearPendingInResults(resultsArray);
  const isComplaintAllowed = !is2018EndYearPending;

  // Helper function to convert total_score string to number
  const getScoreNumber = (score) => {
    if (score === null || score === undefined) return 0;
    if (typeof score === 'string') {
      const parsed = parseFloat(score);
      return isNaN(parsed) ? 0 : parsed;
    }
    return typeof score === 'number' ? score : 0;
  };

  const isNgValue = (value) =>
    typeof value === 'string' && value.trim().toUpperCase() === 'NG';

  const isNgResult = (resultItem) =>
    resultItem?.status === 'NG' || isNgValue(resultItem?.total_score);

  const formatTotalScore = (resultItem) => {
    if (isNgResult(resultItem)) return 'NG';
    return resultItem?.total_score !== null && resultItem?.total_score !== undefined
      ? `${resultItem.total_score}%`
      : '0%';
  };

  const handleComplaintSubmit = async () => {
    if (is2018EndYearPendingInResults(result?.results)) {
      setValidationError('Complaints are unavailable while your 2018 End Year result is on pending.');
      return;
    }

    if (!complaintData.category || !complaintData.description.trim() || !complaintData.phoneNumber.trim()) {
      setValidationError('Please fill in all required fields.');
      return;
    }

    // Validate phone number length (max 10 characters)
    if (complaintData.phoneNumber.trim().length > 10) {
      setValidationError('Phone number must not exceed 10 characters.');
      return;
    }

    setValidationError('');
    setIsSubmittingComplaint(true);

    try {
      const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
      const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

      const response = await fetch(`${API_URL}/complaints/submit`, {
        method: 'POST',
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          username: result.username,
          full_name: result.full_name || 'N/A',
          category: complaintData.category,
          description: complaintData.description,
          phoneNumber: complaintData.phoneNumber
        })
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setShowComplaintModal(false);
        setShowSuccessModal(true);
        setComplaintData({ category: '', description: '', phoneNumber: '' });
        setValidationError('');
        setComplaintReference(data.data?.reference_number || 'N/A');
      } else {
        setValidationError(data.message || 'Failed to submit complaint. Please try again.');
      }
    } catch (error) {
      console.error('Complaint submission error:', error);
      setValidationError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmittingComplaint(false);
    }
  };

  const handleCancelComplaint = () => {
    setShowComplaintModal(false);
    setComplaintData({ category: '', description: '', phoneNumber: '' });
    setValidationError('');
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setComplaintReference('');
  };

  const copyReferenceNumber = async () => {
    try {
      await navigator.clipboard.writeText(complaintReference);
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2000);
    } catch (err) {
      console.error('Failed to copy reference number:', err);
      alert('Failed to copy reference number. Please copy it manually.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8">
        {/* Back Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-blue-600 hover:text-blue-800 transition-colors"
          >
            <i className="fa-solid fa-arrow-left mr-2" style={{ color: 'rgb(18,82,166)' }}></i>
            <span>Back to Home</span>
          </button>
        </div>

        {/* Certificate Verified Card */}
        <section className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fa-solid fa-check text-2xl text-green-500"></i>
              </div>
              <h3 className="text-lg font-semibold text-green-800 mb-2">Exit Exam Certificate Verified</h3>
              <p className="text-green-700 text-sm">
                This certificate has been successfully verified through the official Ministry of Education portal.
              </p>
            </div>
          </div>
        </section>

        {/* Student Info Card */}
        <section className="bg-white rounded-2xl shadow-lg mb-8 p-6 sm:p-8 border border-gray-100" style={{ fontFamily: 'Roboto, "Helvetica Neue", Arial, sans-serif' }}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: 'fa-user', label: 'Username', value: result.username },
              { icon: 'fa-id-card', label: 'Full Name', value: result.full_name || 'N/A' },
              { icon: 'fa-venus-mars', label: 'Gender', value: result.gender || 'N/A' },
              { icon: 'fa-building-columns', label: 'Institution', value: result.institution_name || 'N/A' },
              { icon: 'fa-book', label: 'Exam Topic', value: result.exam_topic_name || 'N/A' },
            ].map(({ icon, label, value }) => (
              <div key={label} className="p-4 rounded-xl bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="flex items-center mb-2">
                  <i className={`fa-solid ${icon} mr-2`} style={{ color: 'rgb(18,82,166)' }} />
                  <p className="text-sm text-gray-500">{label}</p>
                </div>
                <p className="font-normal text-gray-900 break-words">{value}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Results Table */}
        <section className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-8">
          {/* Check if the most recent result (first after sorting) is disqualified */}
          {resultsArray.length > 0 && resultsArray[0].is_disqualified === 1 && (
            <div className="bg-red-50 border-b border-red-200 p-4">
              <div className="flex items-center justify-center">
                <i className="fa-solid fa-ban text-red-500 mr-3 text-xl"></i>
                <span className="text-red-700 font-bold text-lg">DISQUALIFIED</span>
              </div>
            </div>
          )}
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr style={{ backgroundColor: 'rgb(18,82,166)' }}>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Examination Year</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Total Score</th>
                  <th className="px-6 sm:px-8 py-4 sm:py-5 text-left text-sm font-bold text-white uppercase tracking-wider border-b border-white border-opacity-20">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resultsArray && resultsArray.length > 0 ? (
                  resultsArray.map((resultItem, index) => {
                    if (!resultItem) return null;
                    const scoreNum = getScoreNumber(resultItem.total_score);
                    return (
                      <tr key={index} className="hover:bg-gray-50 transition-colors duration-200">
                        <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                          <div className="flex items-center">
                            <i className="fa-regular fa-calendar mr-3 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                            <span className="text-sm font-medium text-gray-900">{resultItem.year || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {formatTotalScore(resultItem)}
                            </div>
                            {!isNgResult(resultItem) && (
                              <div className="ml-3 w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full ${
                                    scoreNum >= 50 ? 'bg-green-600' : 'bg-red-600'
                                  }`}
                                  style={{ width: `${Math.min(scoreNum || 0, 100)}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 sm:px-8 py-4 sm:py-6 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${
                            resultItem.status === 'Pass' 
                              ? 'bg-green-50 text-green-700 border border-green-100' 
                              : 'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            <i className={`fa-solid ${resultItem.status === 'Pass' ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                            {resultItem.status || 'Not Available'}
                            {resultItem.is_disqualified === 1 && (
                              <span className="ml-1 font-bold">(Disqualified)</span>
                            )}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 sm:px-8 py-8 text-center text-gray-500">
                      No results found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden">
            <div className="p-4">
              {resultsArray && resultsArray.length > 0 ? (
                resultsArray.map((resultItem, index) => {
                  if (!resultItem) return null;
                  const scoreNum = getScoreNumber(resultItem.total_score);
                  return (
                    <div key={index} className="mb-4 last:mb-0">
                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <i className="fa-regular fa-calendar mr-2 text-lg" style={{ color: 'rgb(18,82,166)' }} />
                            <span className="text-sm font-medium text-gray-700">Examination Year</span>
                          </div>
                        </div>
                        <p className="text-base font-semibold text-gray-900">{resultItem.year || 'N/A'}</p>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Total Score</span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-base font-semibold text-gray-900">
                            {formatTotalScore(resultItem)}
                          </p>
                        </div>
                        <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                          {!isNgResult(resultItem) && (
                            <div 
                              className={`h-full rounded-full ${
                                scoreNum >= 50 ? 'bg-green-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${Math.min(scoreNum || 0, 100)}%` }}
                            />
                          )}
                        </div>
                      </div>

                      <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-gray-700">Status</span>
                        </div>
                        <div className="space-y-2">
                          <span className={`inline-flex items-center px-3 py-2 rounded-full text-sm font-medium ${
                            resultItem.status === 'Pass' 
                              ? 'bg-green-50 text-green-700 border border-green-100' 
                              : 'bg-red-50 text-red-700 border border-red-100'
                          }`}>
                            <i className={`fa-solid ${resultItem.status === 'Pass' ? 'fa-check-circle' : 'fa-times-circle'} mr-2`} />
                            {resultItem.status || 'Not Available'}
                            {resultItem.is_disqualified === 1 && (
                              <span className="ml-1 font-bold">(Disqualified)</span>
                            )}
                          </span>
                        </div>
                      </div>
                      
                      {index < resultsArray.length - 1 && (
                        <div className="border-t border-gray-200 my-4"></div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No results found.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Note Section with Complaint Button */}
        <section className="mt-12 p-6 sm:p-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 shadow-md">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start">
              <i className="fa-solid fa-circle-info text-2xl flex-shrink-0 mt-1" style={{ color: 'rgb(18,82,166)' }} />
              <div className="ml-4">
                <h4 className="text-lg font-semibold mb-2" style={{ color: 'rgb(18,82,166)' }}>Important Note</h4>
                <p className="text-sm mb-2" style={{ color: 'rgb(18,82,166)' }}>
                  This is your official examination result. For any discrepancies or concerns, please submit a complaint.
                  {is2018EndYearPending ? ' Complaints are unavailable while your 2018 End Year result is on pending.' : ''}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button 
                onClick={() => isComplaintAllowed && setShowComplaintModal(true)}
                className={`flex items-center justify-center px-6 py-3 rounded-lg shadow-md transition-colors ${
                  isComplaintAllowed ? 'text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
                style={isComplaintAllowed ? { backgroundColor: 'rgb(18,82,166)' } : undefined}
                onMouseEnter={(e) => {
                  if (isComplaintAllowed) e.target.style.backgroundColor = 'rgb(15,70,140)';
                }}
                onMouseLeave={(e) => {
                  if (isComplaintAllowed) e.target.style.backgroundColor = 'rgb(18,82,166)';
                }}
                disabled={!isComplaintAllowed}
              >
                <i className="fa-solid fa-flag mr-2" />
                {isComplaintAllowed ? 'Submit Complaint' : 'Complaint unavailable'}
              </button>
            </div>
          </div>
        </section>

      </main>

      {/* Complaint Modal */}
      {showComplaintModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-flag mr-3" style={{ color: 'rgb(18,82,166)' }}></i>
                  Submit Complaint
                </h2>
                <button
                  onClick={handleCancelComplaint}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <form onSubmit={(e) => { e.preventDefault(); handleComplaintSubmit(); }}>
                {/* Category Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Complaint Category *
                  </label>
                  <select
                    value={complaintData.category}
                    onChange={(e) => setComplaintData({ ...complaintData, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select a category</option>
                    {complaintCategories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description * ({complaintData.description.length}/255)
                  </label>
                  <textarea
                    value={complaintData.description}
                    onChange={(e) => setComplaintData({ ...complaintData, description: e.target.value })}
                    rows={4}
                    maxLength={255}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      complaintData.description.length > 250 ? 'border-red-300' : 'border-gray-300'
                    }`}
                    placeholder="Please describe your complaint in detail..."
                    required
                  />
                  {complaintData.description.length > 250 && (
                    <p className="text-sm text-red-600 mt-1">
                      Warning: You're approaching the character limit ({complaintData.description.length}/255)
                    </p>
                  )}
                </div>

                {/* Phone Number */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number * (Format: 09*********)
                  </label>
                  <input
                    type="tel"
                    value={complaintData.phoneNumber}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 10) {
                        setComplaintData({ ...complaintData, phoneNumber: value });
                      }
                    }}
                    maxLength={10}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="09*********"
                    required
                  />
                  {complaintData.phoneNumber.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {complaintData.phoneNumber.length}/10 characters
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {validationError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{validationError}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleCancelComplaint}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingComplaint || !isComplaintAllowed}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingComplaint ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                        Submitting...
                      </>
                    ) : (
                      'Submit Complaint'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            {/* Success Modal Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <i className="fa-solid fa-check-circle mr-3 text-green-500"></i>
                  Success
                </h2>
                <button
                  onClick={handleCloseSuccessModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <i className="fa-solid fa-times text-xl"></i>
                </button>
              </div>
            </div>

            {/* Success Modal Body */}
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fa-solid fa-check text-2xl text-green-500"></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Complaint Submitted
                </h3>
                <p className="text-gray-600 mb-6">
                  Your complaint has been submitted successfully. We have received your request and will review it shortly.
                </p>
                <div className="p-4 mb-4 rounded-lg border text-center" style={{ 
                  backgroundColor: 'rgba(18, 82, 166, 0.05)', 
                  borderColor: 'rgba(18, 82, 166, 0.2)'
                }}>
                  <p className="text-sm font-semibold mb-3" style={{ color: 'rgb(18,82,166)' }}>
                    ⚠️ Save This Reference Number:
                  </p>
                  <div className="flex items-center justify-center mb-3">
                    <span className="font-mono font-bold text-base mr-3" style={{ color: 'rgb(18,82,166)' }}>{complaintReference}</span>
                    <button
                      onClick={copyReferenceNumber}
                      className="px-3 py-1 rounded text-sm font-medium hover:bg-blue-100 transition-colors"
                      style={{ color: 'rgb(18,82,166)' }}
                      title="Copy reference number"
                    >
                      <i className="fa-solid fa-copy mr-1"></i>
                      Copy
                    </button>
                    {copyConfirmed && (
                      <span className="ml-2 text-sm font-medium text-green-600 flex items-center">
                        <i className="fa-solid fa-check mr-1"></i>
                        Copied!
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    You can check the complaint result when announced using this reference number.
                  </p>
                </div>
                <button
                  onClick={handleCloseSuccessModal}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExitExamVerificationPage;
